import { useEffect, useMemo, useRef } from 'react';
import { useGlobalContext } from 'context/GlobalProvider';
import { EEntryType, ESearchParams, EYandexMetrikaEvent } from 'common/const';
import { useSearchParamsHook } from 'common/hooks/useSearchParamsHook';
import { getLandingParams, resolveFunnelEntry } from 'common/helpers/metrika.helper';
import { sendYandexMetrikaEvent } from 'common/helpers/yandex.helper';
import { IOrganization } from 'common/types/organization.types';

/* Источник перехода и вход экрана воронки (metrika-landing, R-1..R-3; payment-page-analytics.md).
 *  Источник - по адресу (utm_source, source, продолжение по снимку); смена других параметров адреса
 *  (visitor_uid, review) и новый снимок при оплате его не меняют. Вход решается по адресу открытия экрана
 *  и только у первого экрана воронки в загрузке страницы: переходы внутри страницы, «назад» на список
 *  и восстановление из кэша браузера landingOpened не дают. */
export const useFunnelEntry = () => {
  const { getSearchParam } = useSearchParamsHook();
  const { claimFirstScreen } = useGlobalContext();
  const utmSource = getSearchParam(ESearchParams.UtmSource);
  const rawSource = getSearchParam(ESearchParams.Source);
  const waiterUuid = getSearchParam(ESearchParams.UserId);

  const entry = useMemo(
    () => resolveFunnelEntry({ utmSource, source: rawSource, waiterUuid }, Date.now()),
    [utmSource, rawSource, waiterUuid],
  );
  // Адрес открытия экрана: первое вычисление, последующие изменения адреса вход не создают
  const openedAsEntryRef = useRef<boolean>(entry.isEntry);
  const landingPendingRef = useRef<boolean>(false);

  useEffect(() => {
    landingPendingRef.current = claimFirstScreen() && openedAsEntryRef.current;
  }, []);

  /* landingOpened после ответа о заведении (у адреса официанта - и об официанте), не больше одного раза. */
  const sendLandingOpened = (organization: IOrganization, entryType: EEntryType) => {
    if (!landingPendingRef.current) {
      return;
    }

    landingPendingRef.current = false;
    sendYandexMetrikaEvent(EYandexMetrikaEvent.LandingOpened, getLandingParams(organization, entry.source, entryType));
  };

  return { source: entry.source, sendLandingOpened };
};
